import { Box, Input as InputNimbus } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { Domain, AiFeature } from '@tiendanube/common/src/enums';
import FeatureFlag from 'App/components/FeatureFlag';
import { FEATURE_AI_TIER } from 'App/features';
import {
  AI_PRODUCT_SEO_BRAND_TAGS_ENABLED,
  CATALOG_PRODUCT_HASHED_SLUG,
} from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { Input } from 'commons/components';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import { useTranslationCommon } from 'commons/hooks';
import { useAiUsage } from 'domains/Ai/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import type { AIGeneratedType } from 'domains/Catalog/Products/productsSlice';
import TagAndSeoItemTitle from '../TagAndSeoItemTitle';

export type onChangeBrandType = ({
  name,
  value,
}: {
  name: string;
  value: string;
}) => void;

interface BrandProps {
  value: string;
  disabled: boolean;
  aiGenerated: boolean;
  missingNameAndDescription: boolean;
  onChange: onChangeBrandType;
  onClickGenerateWithAI: (type: AIGeneratedType) => void;
}

function Brand({
  value,
  disabled,
  aiGenerated,
  missingNameAndDescription,
  onChange,
  onClickGenerateWithAI,
}: Readonly<BrandProps>): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const tCommon = useTranslationCommon();
  const { requiredTier, aiUsesUpsellTitle } = useAiUsage();
  const [hasCatalogProductHashedSlug] = useHasTags([
    CATALOG_PRODUCT_HASHED_SLUG,
  ]);

  const getTooltipText = () => {
    if (value.trim() !== '') return tCommon('aiGeneration.alreadyExists.brand');
    if (missingNameAndDescription)
      return tCommon('aiGeneration.addNameAndDescriptionToGenerate');
    return '';
  };
  const tooltipText = getTooltipText();

  const upsellBrand = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCT_BRAND,
    callback: () => onClickGenerateWithAI('brand'),
    minValue: requiredTier,
    asAside: !hasCatalogProductHashedSlug,
  });

  const label = hasCatalogProductHashedSlug
    ? ''
    : t('products.detail.brand.name');

  const description = hasCatalogProductHashedSlug
    ? t('products.detail.brand.description')
    : '';

  const ariaLabel = label ? '' : description;

  const generateWithAiLink = (
    <FeatureFlag flag={AI_PRODUCT_SEO_BRAND_TAGS_ENABLED}>
      <GenerateWithAiLink
        onClick={upsellBrand}
        fontSize="caption"
        trackingFeature={AiFeature.AI_PRODUCT_BRAND}
        disabled={!!tooltipText}
        tooltipText={tooltipText}
      />
    </FeatureFlag>
  );

  return (
    <TagAndSeoItemTitle
      title={t('products.detail.brand.title')}
      description={description}
      slot={generateWithAiLink}
    >
      {disabled ? (
        <Box width="100%">
          <InputNimbus.Skeleton width="100%" />
        </Box>
      ) : (
        <Input
          label={label}
          placeholder={t('products.detail.brand.placeholder')}
          onChange={onChange}
          value={value}
          name="brand"
          disabled={false}
          aiGenerated={aiGenerated}
          marginBottom="none"
          aria-label={ariaLabel}
        />
      )}
    </TagAndSeoItemTitle>
  );
}

export default Brand;
